import { calculateFare, type VehicleType } from '@raahi/shared';
import { DEMO_DRIVERS, DEMO_FARE_CONFIG } from './data.js';
import { DEMO_TOKENS, DEMO_USER } from './config.js';
import { demoState } from './store.js';

interface MockOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function delay<T>(value: T, ms = 220): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export async function mockRequest<T>(path: string, opts: MockOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const url = new URL(path, 'http://demo.local');
  const route = url.pathname;
  const body = (opts.body as Record<string, unknown>) ?? {};

  if (route === '/auth/login' || route === '/auth/register') {
    return delay({ user: DEMO_USER, ...DEMO_TOKENS } as unknown as T);
  }
  if (route === '/auth/refresh') {
    return delay(DEMO_TOKENS as unknown as T);
  }
  if (route === '/auth/logout') {
    return delay({ ok: true } as unknown as T);
  }
  if (route === '/auth/me') {
    return delay(DEMO_USER as unknown as T);
  }

  if (route === '/pricing/estimate' && method === 'GET') {
    const params = opts.query ?? {};
    const type = String(params.type ?? 'economy') as VehicleType;
    const distanceKm = haversine(
      { lat: Number(params.pickupLat ?? 0), lng: Number(params.pickupLng ?? 0) },
      { lat: Number(params.dropLat ?? 0), lng: Number(params.dropLng ?? 0) },
    );
    const durationMin = Math.max(1, Math.round((distanceKm / 28) * 60));
    const fare = calculateFare(DEMO_FARE_CONFIG[type], distanceKm, durationMin, 1);
    return delay({ distanceKm, durationMin, surgeMultiplier: 1, fare } as unknown as T);
  }

  if (route === '/drivers/nearby' && method === 'GET') {
    const params = opts.query ?? {};
    const wanted = params.type as VehicleType | undefined;
    const subset = wanted ? DEMO_DRIVERS.filter((d) => d.vehicle.type === wanted) : DEMO_DRIVERS;
    return delay(
      subset.slice(0, 3).map((d) => ({
        id: d.id,
        lat: d.position.lat,
        lng: d.position.lng,
        heading: 0,
        vehicleType: d.vehicle.type,
      })) as unknown as T,
    );
  }

  if (route === '/trips' && method === 'POST') {
    const trip = demoState.request(body as Parameters<typeof demoState.request>[0]);
    return delay(trip as unknown as T);
  }

  if (route === '/trips/history' && method === 'GET') {
    return delay(demoState.history() as unknown as T);
  }

  const tripIdMatch = /^\/trips\/([^/]+)(\/(cancel|rate))?$/.exec(route);
  if (tripIdMatch) {
    const tripId = tripIdMatch[1]!;
    const tail = tripIdMatch[3];
    if (!tail && method === 'GET') {
      const trip = demoState.trip(tripId);
      if (!trip) throw new Error('Trip not found.');
      return delay(trip as unknown as T);
    }
    if (tail === 'cancel' && method === 'POST') {
      const trip = demoState.cancel(tripId);
      if (!trip) throw new Error('Trip not found.');
      return delay(trip as unknown as T);
    }
    if (tail === 'rate' && method === 'POST') {
      const trip = demoState.rate(tripId);
      if (!trip) throw new Error('Trip not found.');
      return delay(trip as unknown as T);
    }
  }

  if (route === '/payments/intent' && method === 'POST') {
    return delay({ provider: 'demo', clientSecret: 'demo-secret', providerId: 'demo-intent' } as unknown as T);
  }

  throw new Error(`Demo mode: route not mocked yet (${method} ${route}).`);
}

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return Number((2 * R * Math.asin(Math.sqrt(h))).toFixed(2));
}
